import {skipToken} from '@reduxjs/toolkit/query';

import {equals, isNotNil} from 'ramda';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {CustomerResponseBodyV2} from '@dms/api/customer';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {CurrencyResponseBody} from '@dms/api/shared';
import {
  GetReceiveNoteCorrectionResponse,
  GetWarehousesResponse,
  useGetReceiveNoteStateQuery,
} from '@dms/api/warehouse';
import {getCustomerName} from '@dms/shared';
import {getSupplierById, getWarehouseById} from '@dms/warehouse/shared';
import {ReceiveNoteBasicInformationReadOnly} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {BasicInformationForm} from './BasicInformationForm';

interface CorrectionCardProps extends RequiredTestIdProps {
  receiveNoteCorrection: GetReceiveNoteCorrectionResponse | Nullish;
  suppliers: CustomerResponseBodyV2[] | Nullish;
  warehouses: GetWarehousesResponse | Nullish;
  authorizationProfiles: BaseAuthorizationProfile[] | Nullish;
  paymentTypes: PaymentTypeResponseBody[] | Nullish;
  currencies: CurrencyResponseBody[] | Nullish;
  isReadOnly: boolean;
  onClose: VoidFunction;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function CorrectionCard(props: CorrectionCardProps) {
  const isReceiveNoteCorrectionCompleted =
    props.receiveNoteCorrection?.creditNote?.state === 'COMPLETED';

  const correctionSupplier = getCustomerName(
    getSupplierById(props.suppliers ?? [], props.receiveNoteCorrection?.creditNote?.supplierId)
  );

  const correctionWarehouse = getWarehouseById(
    props.warehouses ?? [],
    props.receiveNoteCorrection?.creditNote?.warehouseId
  )?.name;

  const correctionAuthorizationProfile = props.authorizationProfiles?.find((authorizationProfile) =>
    equals(authorizationProfile.id, props.receiveNoteCorrection?.creditNote?.authorizationProfileId)
  )?.name;

  const correctionNumber = props.receiveNoteCorrection?.creditNote?.creditNoteNumber;

  const correctionDate = isReceiveNoteCorrectionCompleted
    ? props.receiveNoteCorrection?.creditNote?.updatedAt
    : null;

  const correctionInvoiceNumber = props.receiveNoteCorrection?.creditNote?.correctionInvoiceNumber;

  const correctionInvoiceIssueDate = props.receiveNoteCorrection?.creditNote?.correctionInvoiceDate;

  const correctionPaymentType = props.paymentTypes?.find((paymentType) =>
    equals(paymentType.key, props.receiveNoteCorrection?.creditNote?.paymentType)
  )?.value;

  const correctionCurrency = props.currencies?.find((currency) =>
    equals(currency.code, props.receiveNoteCorrection?.creditNote?.currency)
  )?.code;

  const correctionNote = props.receiveNoteCorrection?.creditNote?.note;
  const correctionExchangeRate = props.receiveNoteCorrection?.creditNote?.exchangeRate;
  const correctionNcConversion = props.receiveNoteCorrection?.creditNote?.ncConversion;
  const deliveryNoteNumber = props.receiveNoteCorrection?.creditNote?.correctionDeliveryNoteNumber;
  const issuedAt = props.receiveNoteCorrection?.creditNote?.correctionDeliveryNoteDate;

  const sourceReceiveNoteId = props.receiveNoteCorrection?.receiveNote?.receiveNoteId;

  const {data: sourceReceiveNoteState} = useGetReceiveNoteStateQuery(
    isNotNil(sourceReceiveNoteId) && !props.isReadOnly
      ? {receiveNoteId: sourceReceiveNoteId}
      : skipToken
  );

  return props.isReadOnly ? (
    <ReceiveNoteBasicInformationReadOnly
      readOnlyFields={{
        supplierName: correctionSupplier,
        warehouseName: correctionWarehouse,
        authorizationProfileName: correctionAuthorizationProfile,
        number: correctionNumber,
        date: correctionDate,
        invoiceNumber: correctionInvoiceNumber,
        invoiceIssueDate: correctionInvoiceIssueDate,
        paymentType: correctionPaymentType,
        currency: correctionCurrency,
        exchangeRate: correctionExchangeRate,
        ncConversion: correctionNcConversion,
        note: correctionNote,
        deliveryNoteNumber,
        issuedAt,
      }}
      data-testid={suffixTestId('basicInformationReadOnly', props)}
    />
  ) : (
    <BasicInformationForm
      receiveNoteCorrection={props.receiveNoteCorrection}
      sourceReceiveNoteType={sourceReceiveNoteState?.type}
      suppliers={props.suppliers}
      warehouses={props.warehouses}
      authorizationProfiles={props.authorizationProfiles}
      paymentTypes={props.paymentTypes}
      currencies={props.currencies}
      onClose={props.onClose}
      data-testid={suffixTestId('basicInformationForm', props)}
    />
  );
}
